import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Logo from '@/components/feature/Logo';
import Footer from '@/components/feature/Footer';
import { ApiError, lookupAppointment, updateAppointmentDecision } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

interface AppointmentInfo {
  practitioner_name: string | null;
  practitioner_type: string | null;
  appointment_date: string;
  start_time: string;
  patient_name: string;
  patient_phone: string;
  status: string;
}

type LoadState = 'loading' | 'ready' | 'notfound' | 'error';
type DecisionState = 'idle' | 'processing' | 'confirmed' | 'cancelled' | 'error';

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(value: string): string {
  const [h, m] = value.split(':').map(Number);
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
}

export default function ValidateAppointmentPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [appointment, setAppointment] = useState<AppointmentInfo | null>(null);
  const [decisionState, setDecisionState] = useState<DecisionState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setLoadState('notfound');
      return;
    }
    let active = true;
    const load = async () => {
      try {
        const data = await lookupAppointment(token);
        if (!active) return;
        const record = data as AppointmentInfo;
        setAppointment(record);
        setLoadState('ready');
      } catch (err) {
        if (!active) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadState('notfound');
          return;
        }
        setErrorMsg(err instanceof Error ? err.message : 'Une erreur est survenue');
        setLoadState('error');
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const decide = useCallback(
    async (decision: 'confirmed' | 'cancelled') => {
      if (!token) return;
      setDecisionState('processing');
      setErrorMsg('');
      try {
        const response = await updateAppointmentDecision(token, decision);
        const status = String(response.status ?? decision);
        if (status === 'confirmed' || status === 'cancelled') {
          trackEvent('appointment_decided', {
            decision: status,
            specialty: appointment?.practitioner_type ?? '',
          });
          setDecisionState(status);
        } else {
          setErrorMsg('Décision non reconnue.');
          setDecisionState('error');
        }
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Une erreur est survenue');
        setDecisionState('error');
      }
    },
    [token, appointment],
  );

  const alreadyHandled =
    appointment !== null && appointment.status !== 'pending' && decisionState === 'idle';

  return (
    <div className="min-h-screen bg-background-100 flex flex-col">
      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <Link
            to="/"
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            Retour au site
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="max-w-xl mx-auto">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-3">
            Espace praticien
          </span>

          {loadState === 'loading' && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-8 flex flex-col items-center text-center">
              <div className="w-8 h-8 mb-4 flex items-center justify-center rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
              <p className="text-sm text-foreground-600">Chargement de la demande…</p>
            </div>
          )}

          {loadState === 'notfound' && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-5">
                <i className="ri-link-unlink-m text-3xl text-foreground-600 w-8 h-8 flex items-center justify-center" />
              </div>
              <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                Lien invalide
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Ce lien de validation est introuvable ou a expiré. Si vous avez reçu une demande de
                rendez-vous, vérifiez le lien contenu dans le message.
              </p>
            </div>
          )}

          {loadState === 'error' && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-5">
                <i className="ri-error-warning-line text-3xl text-accent-700 w-8 h-8 flex items-center justify-center" />
              </div>
              <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                Erreur de chargement
              </h1>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">{errorMsg}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
                Réessayer
              </button>
            </div>
          )}

          {loadState === 'ready' && appointment && (
            <>
              {(decisionState === 'confirmed' || decisionState === 'cancelled') && (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
                  <div
                    className={`w-16 h-16 mx-auto flex items-center justify-center rounded-full mb-5 ${
                      decisionState === 'confirmed' ? 'bg-primary-100' : 'bg-secondary-100'
                    }`}
                  >
                    <i
                      className={`text-3xl w-8 h-8 flex items-center justify-center ${
                        decisionState === 'confirmed'
                          ? 'ri-check-double-line text-primary-600'
                          : 'ri-close-circle-line text-secondary-700'
                      }`}
                    />
                  </div>
                  <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                    {decisionState === 'confirmed'
                      ? 'Rendez-vous confirmé'
                      : 'Demande refusée'}
                  </h1>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    {decisionState === 'confirmed'
                      ? `Le rendez-vous de ${appointment.patient_name} le ${formatDate(
                          appointment.appointment_date,
                        )} à ${formatTime(appointment.start_time)} est confirmé.`
                      : `Vous avez refusé la demande de ${appointment.patient_name}. Le créneau est de nouveau libre.`}
                  </p>
                  <p className="text-xs text-foreground-500">
                    Merci de prévenir le patient par téléphone au{' '}
                    <strong className="text-foreground-700">{appointment.patient_phone}</strong>.
                  </p>
                </div>
              )}

              {alreadyHandled && (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-5">
                    <i className="ri-information-line text-3xl text-foreground-600 w-8 h-8 flex items-center justify-center" />
                  </div>
                  <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                    Demande déjà traitée
                  </h1>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Ce rendez-vous a déjà le statut «{' '}
                    {appointment.status === 'confirmed' ? 'confirmé' : 'refusé'} ». Aucune action
                    supplémentaire n’est nécessaire.
                  </p>
                </div>
              )}

              {(decisionState === 'idle' || decisionState === 'processing' || decisionState === 'error') &&
                !alreadyHandled && (
                  <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
                    <h1 className="font-heading text-2xl font-semibold text-foreground-950 mb-1">
                      Nouvelle demande de rendez-vous
                    </h1>
                    <p className="text-sm text-foreground-600 mb-6">
                      Vérifiez les informations puis confirmez ou refusez la demande.
                    </p>

                    <div className="rounded-xl bg-background-100 border border-background-200 divide-y divide-background-200 mb-6">
                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                          Patient
                        </span>
                        <span className="text-sm font-semibold text-foreground-900 text-right">
                          {appointment.patient_name}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                          Téléphone
                        </span>
                        <a
                          href={`tel:${appointment.patient_phone.replace(/\s+/g, '')}`}
                          className="text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                        >
                          {appointment.patient_phone}
                        </a>
                      </div>
                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                          Date
                        </span>
                        <span className="text-sm text-foreground-800 text-right">
                          {formatDate(appointment.appointment_date)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                          Heure
                        </span>
                        <span className="text-sm text-foreground-800 text-right">
                          {formatTime(appointment.start_time)}
                        </span>
                      </div>
                    </div>

                    {decisionState === 'error' && errorMsg && (
                      <p className="text-sm text-accent-600 mb-4">{errorMsg}</p>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => decide('confirmed')}
                        disabled={decisionState === 'processing'}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {decisionState === 'processing' ? (
                          <>
                            <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
                            Traitement…
                          </>
                        ) : (
                          <>
                            <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
                            Confirmer le rendez-vous
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => decide('cancelled')}
                        disabled={decisionState === 'processing'}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-300 hover:border-accent-400 hover:text-accent-700 disabled:opacity-50 disabled:cursor-not-allowed text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
                        Refuser
                      </button>
                    </div>

                    <p className="mt-5 text-xs text-foreground-500 flex items-start gap-2 leading-relaxed">
                      <i className="ri-shield-check-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5" />
                      Ce lien est personnel et sécurisé. Il ne donne accès qu’à cette demande de
                      rendez-vous.
                    </p>
                  </div>
                )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}